import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FileUploaderInterface } from '@vendasta/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PersonaType } from '@vendasta/task-manager';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import {
  ProjectTrackerFileUploadServiceInterfaceToken,
  ProjectTrackerServiceInterface,
  ProjectTrackerServiceInterfaceToken,
} from '../../project-tracker-interface';
import { AttachedFile, AttachmentInterface, NonClickableSection, Subtask } from '../interfaces';

@Component({
  selector: 'project-tracker-subtasks',
  templateUrl: './tracker-subtasks.component.html',
  styleUrls: ['./tracker-subtasks.component.scss'],
  standalone: false,
})
export class TrackerSubtasksComponent implements OnInit {
  @Input() subtasks: Subtask[] = [];
  @Output() approveClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() provideFeedbackClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() sendFeedbackClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() nonClickableClicked: EventEmitter<NonClickableSection> = new EventEmitter<NonClickableSection>();
  attachmentUploader: FileUploaderInterface;
  PersonaType = PersonaType;

  constructor(
    @Inject(ProjectTrackerServiceInterfaceToken) private projectTrackerService: ProjectTrackerServiceInterface,
    @Inject(ProjectTrackerFileUploadServiceInterfaceToken)
    private projectTrackerAttachmentService: FileUploaderInterface,
    private alertService: SnackbarService,
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.attachmentUploader = this.projectTrackerAttachmentService;
    this.subtasks
      ?.map((subtask: Subtask) => {
        if (subtask?.feedback && !subtask?.conversations) {
          return {
            ...subtask,
            conversations: subtask.feedback.map((message) => ({ message })),
          };
        }
        return subtask;
      })
      ?.forEach((subtask: Subtask, index: number) => {
        const filesControlName = this.getFilesControlName(index);
        const feedbackControlName = this.getFeedbackControlName(index);
        const controls = {};
        controls[filesControlName] = Object.assign([], subtask.files || Array<AttachedFile>());
        controls[feedbackControlName] = '';
        subtask.form = this.fb.group(controls);
        subtask.form.get(filesControlName).patchValue(Object.assign([], subtask.files || Array<AttachedFile>()));
      });
  }

  getFilesControlName(index: number): string {
    return 'files' + index;
  }
  getFeedbackControlName(index: number): string {
    return 'feedback' + index;
  }

  onFileUploaded(uploadInfo: AttachmentInterface, index: number): void {
    const subtask = this.subtasks[index];
    if (uploadInfo.name === '' || uploadInfo.url === '') {
      this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.ATTACHMENT');
      const current = subtask.form.get(this.getFilesControlName(index)).value;
      current.pop();
      subtask.form.get(this.getFilesControlName(index)).patchValue(current);
      return;
    } else {
      if (!subtask.files) {
        subtask.files = [];
      }
      subtask.approved = false;
      subtask.files.push(uploadInfo);
      this.projectTrackerService
        .uploadFile(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, uploadInfo)
        .pipe(
          take(1),
          switchMap(() =>
            this.alertService
              .openSuccessSnack('PROJECT_TRACKER.SUCCESS.FILE_UPLOAD', {
                action: this.translateService.instant('PROJECT_TRACKER.UNDO'),
              })
              .onAction(),
          ),
          tap(() => this.undoFileUpload(uploadInfo, index)),
          catchError(() => {
            this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.FILE_UPLOAD');
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  undoFileUpload(attachment: AttachmentInterface, index: number): void {
    const subtask = this.subtasks[index];
    this.projectTrackerService
      .removeFile(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, attachment)
      .pipe(take(1))
      .subscribe(
        () => {
          this.alertService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.FILE_UPLOAD_UNDO');
          subtask.files.pop();
          subtask.form.get(this.getFilesControlName(index)).patchValue(Object.assign([], subtask.files));
        },
        () => this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.FILE_UPLOAD_UNDO'),
      );
  }

  onFileError(): void {
    this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.FAIL_TO_ADD');
  }

  onFileRemoved(fileIndex: number, subtaskIndex: number): void {
    const subtask = this.subtasks[subtaskIndex];
    subtask.approved = false;
    const attachment = subtask.files.splice(fileIndex, 1)[0];
    this.projectTrackerService
      .removeFile(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, attachment)
      .pipe(
        take(1),
        switchMap(() =>
          this.alertService
            .openSuccessSnack(this.translateService.instant('PROJECT_TRACKER.SUCCESS.FILE_REMOVE'), {
              action: this.translateService.instant('PROJECT_TRACKER.UNDO'),
            })
            .onAction(),
        ),
        tap(() => this.undoFileRemoved(attachment, subtaskIndex)),
        catchError(() => {
          this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.FILE_REMOVE');
          subtask.files.push(attachment);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  undoFileRemoved(attachment: AttachmentInterface, index: number): void {
    const subtask = this.subtasks[index];
    this.projectTrackerService
      .uploadFile(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, attachment)
      .pipe(take(1))
      .subscribe(
        () => {
          this.alertService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.FILE_REMOVE_UNDO');
          subtask.files.push(attachment);
          subtask.form.get(this.getFilesControlName(index)).patchValue(Object.assign([], subtask.files));
        },
        () => {
          this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.FILE_REMOVE_UNDO');
        },
      );
  }

  approveTask(index: number): void {
    this.approveClicked.emit();
    const subtask = this.subtasks[index];
    subtask.showFeedback = false;
    subtask.approved = true;
    this.projectTrackerService
      .approveTask(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId)
      .pipe(
        take(1),
        switchMap(() =>
          this.alertService
            .openSuccessSnack('PROJECT_TRACKER.SUCCESS.APPROVE_TASK', {
              action: this.translateService.instant('PROJECT_TRACKER.UNDO'),
            })
            .onAction(),
        ),
        tap(() => this.undoApproveTask(subtask)),
        catchError(() => {
          this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.APPROVE_TASK');
          subtask.approved = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  undoApproveTask(subtask: Subtask): void {
    subtask.approved = false;
    this.projectTrackerService
      .undoApproveTask(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId)
      .pipe(take(1))
      .subscribe({
        next: () => this.alertService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.APPROVE_TASK_UNDO'),
        error: () => {
          this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.APPROVE_TASK_UNDO');
          subtask.approved = true;
        },
      });
  }

  saveFeedback(index: number): void {
    this.sendFeedbackClicked.emit();
    const subtask = this.subtasks[index];
    subtask.showFeedback = false;
    subtask.approved = false;
    if (!subtask?.conversations?.length) {
      subtask.conversations = [];
    }
    const feedback = subtask.form.get(this.getFeedbackControlName(index)).value;
    subtask.conversations.push({
      message: feedback,
      sendTo: 'Digital Agent',
      source: 'smb',
      created: new Date(),
    });
    this.projectTrackerService
      .sendFeedback(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, feedback)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.FEEDBACK_PROVIDED');
          subtask.form.get(this.getFeedbackControlName(index)).patchValue('');
        },
        error: () => {
          this.alertService.openErrorSnack('PROJECT_TRACKER.ERROR.FEEDBACK_PROVIDED');
          subtask.conversations.pop();
        },
      });
  }

  toggleShowFeedback(index: number): void {
    this.subtasks[index].showFeedback = !this.subtasks[index].showFeedback;
    if (this.subtasks[index].showFeedback) {
      this.provideFeedbackClicked.emit();
    }
  }

  handleNonClickableClicked(event: NonClickableSection): void {
    this.nonClickableClicked.emit(event);
  }
}
